import { inject, Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { Store } from '@ngrx/store';
import { combineLatest, first, map, Observable, of, startWith } from 'rxjs';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { ElementActions, ElementTypes, GaContext } from '@fcom/common';
import { orderData } from '@fcom/common-booking';
import {
  bookingHasDisruption,
  isBoundAPISRequired,
  isBoundFlown,
  isDisrupted,
  isFlight,
} from '@fcom/common-booking/utils/common-booking.utils';
import { GtmService } from '@fcom/common/gtm';
import { AppState, RootPaths, TzDate } from '@fcom/core';
import {
  FinnairBoundItem,
  FinnairCancelIneligibilityReason,
  FinnairChangeIneligibilityReason,
  FinnairCheckInVersion,
  FinnairItineraryItemFlight,
  FinnairItineraryItemFlightStatus,
  FinnairOrder,
} from '@fcom/dapi/api/models';
import { getCityName } from '@fcom/common/utils/booking.utils';
import { NotificationLayout, NotificationTheme } from '@fcom/ui-components';
import { LanguageService } from '@fcom/ui-translate';
import { stopPropagation } from '@fcom/core/utils';

import { OPEN_TRAVEL_PREPARATIONS_NOTIFICATION } from '../constants';
import { BoundStatus } from '../enums';
import { NotificationInfo, Notifications } from '../interfaces';
import {
  getItinerariesWithMixedCabinClasses,
  isBoundWithCheckInOpenAndNotAllAreCheckedIn,
  shouldShowTrainCheckInNotification,
  travelRequirementsRequired,
} from '../utils';

const changeIneligibilityTranslations: Map<FinnairChangeIneligibilityReason, string> = new Map([
  [FinnairChangeIneligibilityReason.AMADEUS_REASON, 'changeNotAllowed.amadeusReason'],
  [FinnairChangeIneligibilityReason.AMADEUS_TICKET_PROBLEM, 'changeNotAllowed.amadeusTicketProblem'],
  [FinnairChangeIneligibilityReason.AMADEUS_UNISSUED_SERVICE_PRESENT, 'changeNotAllowed.amadeusUnissuedServicePresent'],
  [
    FinnairChangeIneligibilityReason.AMADEUS_COMPLEX_ITINERARY_NOT_ALLOWED,
    'changeNotAllowed.amadeusComplexItineraryNotAllowed',
  ],
  [FinnairChangeIneligibilityReason.AMADEUS_FLIGHT_GENERAL_STATUS, 'changeNotAllowed.amadeusFlightGeneralStatus'],
  [FinnairChangeIneligibilityReason.AMADEUS_DISCREPANCY_IN_ORDER, 'changeNotAllowed.amadeusDiscrepancyInOrder'],
  [FinnairChangeIneligibilityReason.AMADEUS_OFFER_CONDITION_RULE, 'changeNotAllowed.amadeusOfferConditionRule'],
  [FinnairChangeIneligibilityReason.AMADEUS_FLIGHT_DISRUPTED, 'changeNotAllowed.amadeusFlightDisrupted'],
  [FinnairChangeIneligibilityReason.FLIGHT_DISRUPTED_BY_TIME_CHANGE, 'changeNotAllowed.amadeusFlightTimeChanged'],
  [FinnairChangeIneligibilityReason.FLIGHT_DISRUPTED_BY_CANCEL, 'changeNotAllowed.amadeusFlightCancelled'],
  [FinnairChangeIneligibilityReason.AWARD_BOOKING, 'changeNotAllowed.awardBooking'],
  [FinnairChangeIneligibilityReason.TOUROPERATOR_TICKETS, 'changeNotAllowed.tourOperatorBooking'],
  [FinnairChangeIneligibilityReason.BOUND_ALREADY_CHECKED_IN, 'changeNotAllowed.boundAlreadyCheckedIn'],
  [FinnairChangeIneligibilityReason.BOUND_ALREADY_FLOWN_OR_USED, 'changeNotAllowed.boundAlreadyFlownOrUsed'],
  [FinnairChangeIneligibilityReason.ID_TICKETS, 'changeNotAllowed.idTickets'],
  [FinnairChangeIneligibilityReason.NOT_ELIGIBLE_SSR, 'changeNotAllowed.notEligibleSSR'],
  [FinnairChangeIneligibilityReason.AMADEUS_GROUPS_NOT_SUPPORTED, 'changeNotAllowed.amadeusGroupNotSupported'],
  [FinnairChangeIneligibilityReason.PETC_FROM_TO_AGP, 'changeNotAllowed.petcFromToAGP'],
  [FinnairChangeIneligibilityReason.CHECK_IN_CLOSED_AND_PAX_CHECKED_IN, 'changeNotAllowed.checkInClosedAndPaxAccepted'],
]);

const voluntaryCancelIneligibilityTranslations: Map<FinnairCancelIneligibilityReason, string> = new Map([
  [FinnairCancelIneligibilityReason.TRAVEL_AGENT_TICKETS, 'cancelNotAllowed.travelAgent'],
  [FinnairCancelIneligibilityReason.NOT_TICKETED, 'cancelNotAllowed.notTicketed'],
  [FinnairCancelIneligibilityReason.NOT_ELIGIBLE, 'cancelNotAllowed.notEligible'],
  [FinnairCancelIneligibilityReason.ID_TICKET, 'cancelNotAllowed.idTicket'],
  [FinnairCancelIneligibilityReason.ORDER_WITH_NO_MAIN_TRAVELER, 'cancelNotAllowed.orderWithNoMainTraveler'],
  [FinnairCancelIneligibilityReason.ALL_FLIGHTS_ALREADY_FLOWN, 'cancelNotAllowed.allFlightsAlreadyFlown'],
  [FinnairCancelIneligibilityReason.NOT_ELIGIBLE_EMAIL_MISSING, 'cancelNotAllowed.emailMissing'],
  [
    FinnairCancelIneligibilityReason.CHECK_IN_CLOSED_AND_PAX_CHECKED_IN,
    'cancelNotAllowed.checkInClosedAndPaxCheckedIn',
  ],
  [
    FinnairCancelIneligibilityReason.CHECKED_IN_AND_BOARDED_OR_BAGGAGE_DROPPED,
    'cancelNotAllowed.checkedInAndBoardedOrBaggageDropped',
  ],
]);

@Injectable()
export class NotificationsService {
  private store$ = inject<Store<AppState>>(Store);
  private languageService = inject(LanguageService);
  private gtmService = inject(GtmService);
  private activatedRoute = inject(ActivatedRoute);

  getNotificationsForBound(
    order: FinnairOrder,
    bound: FinnairBoundItem,
    boundStatus: BoundStatus
  ): Observable<Notifications> {
    const shouldShowCheckInNotificationWhenOpen: boolean = isBoundWithCheckInOpenAndNotAllAreCheckedIn(order, bound);

    return this.store$.pipe(orderData()).pipe(
      first(),
      map((order) => {
        const locations = order?.locations;
        const origin = getCityName(bound.departure.locationCode, locations);
        const destination = getCityName(bound.arrival.locationCode, locations);
        const itinerariesWithMixedCabinClasses = getItinerariesWithMixedCabinClasses(bound);
        const waitlistedItineraries = bound.itinerary
          .filter(isFlight)
          .filter(
            (itinerary) =>
              itinerary.status === FinnairItineraryItemFlightStatus.WAITLISTED ||
              itinerary.upgradeItem?.status === FinnairItineraryItemFlightStatus.WAITLISTED
          );
        const isBookingHasDisruption = bookingHasDisruption(order);
        const isFirstBound = order.bounds.findIndex((b) => b.id === bound.id) === 0;
        const isShowApisRequiredBound =
          !shouldShowCheckInNotificationWhenOpen &&
          isBoundAPISRequired(order, bound) &&
          !isBookingHasDisruption &&
          !isFirstBound;
        return [
          {
            iconName: SvgLibraryIcon.CHECK_IN,
            translationKey: 'checkInOpen',
            show: shouldShowCheckInNotificationWhenOpen,
            theme: NotificationTheme.INFO,
            replace: shouldShowCheckInNotificationWhenOpen && this.getLocationData(bound, locations),
          },
          {
            translationKey: 'originalFlight',
            show: boundStatus === BoundStatus.ORIGINAL,
            theme: NotificationTheme.ALERT,
            replace: { origin, destination },
          },
          {
            translationKey: 'newFlight',
            show: boundStatus === BoundStatus.NEW,
            theme: NotificationTheme.ALERT,
            replace: { origin, destination },
          },
          {
            iconName: SvgLibraryIcon.AIRCRAFT_TILTED,
            translationKey: 'flownFlight',
            show: isBoundFlown(bound),
            theme: NotificationTheme.INFO,
          },
          {
            iconName: SvgLibraryIcon.TRAVEL_DOCUMENTS,
            translationKey: 'upcomingBoundAPIS',
            show: isShowApisRequiredBound,
            theme: NotificationTheme.INFO,
            hasInnerHTML: true,
          },
          ...waitlistedItineraries.map((itinerary) => ({
            translationKey: this.getWaitlistUpgradeTranslationKey(itinerary),
            show: true,
            theme: NotificationTheme.INFO,
            iconName: SvgLibraryIcon.TIME_ZONE,
            replace: {
              origin: getCityName(itinerary.departure.locationCode, locations),
              destination: getCityName(itinerary.arrival.locationCode, locations),
            },
          })),
          ...itinerariesWithMixedCabinClasses.map((itinerary) => ({
            translationKey: 'mixedCabinClass',
            show: true,
            theme: NotificationTheme.INFO,
            replace: {
              origin: getCityName(itinerary.departure.locationCode, locations),
              destination: getCityName(itinerary.arrival.locationCode, locations),
            },
          })),
        ].filter((n) => n.show);
      })
    );
  }

  /**
   * Returns notifications for trip card (to be shown on top of the image)
   *
   */
  getNotificationsForTrip(
    router: Router,
    callbackFnIfPassengerDoesNotHaveEmail?: () => void
  ): Observable<Notifications> {
    return combineLatest([
      this.store$.pipe(orderData()),
      this.languageService.lang,
      this.languageService.translate('MMB.notifications.tripContainsTrain.link'),
    ]).pipe(
      first(),
      map(([order, language, tripContainsTrainLink]) => {
        const fromCheckin = this.activatedRoute.snapshot.queryParamMap.get('fromCheckIn');
        const passengers = order?.passengers;
        const isApisRequired = travelRequirementsRequired(order) && !bookingHasDisruption(order);

        return [
          {
            iconName: SvgLibraryIcon.ALERT_BLOCK,
            translationKey: 'boundDisrupted',
            theme: NotificationTheme.ALERT,
            show: isDisrupted(order),
            linkTarget: `/${language}/${RootPaths.DISRUPTION_ROOT}`,
            clickFn: (event) => {
              stopPropagation(event);
              if (!passengers[0]?.email && callbackFnIfPassengerDoesNotHaveEmail) {
                callbackFnIfPassengerDoesNotHaveEmail();
              } else {
                router.navigateByUrl(`/${language}/${RootPaths.DISRUPTION_ROOT}`);
              }
            },
          },
          {
            iconName: SvgLibraryIcon.CHECK_IN,
            translationKey: 'checkInNotOpenYet',
            show:
              order?.eligibilities?.checkIn?.some(
                (eligibility) =>
                  eligibility.isAllowedToUseCheckIn === false && eligibility.version === FinnairCheckInVersion.LEGACY
              ) &&
              fromCheckin &&
              fromCheckin === 'true',
            theme: NotificationTheme.INFO,
          },
          {
            iconName: SvgLibraryIcon.TRAVEL_DOCUMENTS,
            translationKey: 'provideApisBeforeCheckIn',
            show: isApisRequired,
            theme: NotificationTheme.WARNING,
            linkTarget: `/${language}/manage/view/travel-preparations`,
            clickFn: (event) => {
              stopPropagation(event);
              router.navigateByUrl(`/${language}/manage/view/travel-preparations`);
              this.gtmService.trackElement(
                OPEN_TRAVEL_PREPARATIONS_NOTIFICATION,
                GaContext.MANAGE_BOOKING,
                ElementTypes.LINK,
                undefined,
                ElementActions.CLICK
              );
            },
          },
          {
            iconName: SvgLibraryIcon.INFO_BLOCK,
            translationKey: 'tripContainsTrain',
            theme: NotificationTheme.INFO,
            show: shouldShowTrainCheckInNotification(order),
            linkTarget: tripContainsTrainLink,
            hasInnerHTML: true,
            linkOpenInNewWindow: true,
          },
        ].filter((n) => n.show);
      })
    );
  }

  /**
   * Returns notifications for trip card (to be shown on top of the trip card)
   *
   * Should return ticket change policy notification when we have the specs for logic and translations
   */
  getTopNotificationsForTrip(): Observable<Notifications> {
    return of([]);
  }

  /**
   * Returns notifications with reasons for flight changes not being allowed for whole trip
   *
   */
  getModificationNotAllowedNotificationsForTrip(): Observable<Notifications> {
    return this.store$.pipe(
      orderData(),
      map((booking) => {
        // we only want to return notifications if changes are disallowed for the whole trip, i.e. all bounds and the bookings is not disrupted

        if (
          bookingHasDisruption(booking) ||
          booking.eligibilities.change.map((eligibility) => eligibility.isAllowedToUse).some(Boolean)
        ) {
          return [];
        }

        if (
          booking.eligibilities?.voluntaryCancel?.some(
            (e) => e.reason === FinnairCancelIneligibilityReason.CHECK_IN_CLOSED_AND_PAX_CHECKED_IN
          )
        ) {
          return [this.getNotification('changeNotAllowed.checkInClosedAndPaxAccepted')];
        }

        const uniqueDisallowedReasons: FinnairChangeIneligibilityReason[] = [
          ...new Set(
            booking.eligibilities.change
              .filter((eligibility) => !eligibility.isAllowedToUse)
              .map((eligibility) => eligibility.reason)
          ),
        ];

        return uniqueDisallowedReasons.map((reason) => {
          return this.getNotification(
            changeIneligibilityTranslations.get(reason) ?? 'changeNotAllowed',
            SvgLibraryIcon.ALERT_BLOCK
          );
        });
      })
    );
  }

  getCancellationNotAllowedNotificationsForTrip(): Observable<Notifications> {
    return this.store$.pipe(
      orderData(),
      map((booking) => {
        if (bookingHasDisruption(booking)) {
          return [];
        }

        const uniqueDisallowedReasons: FinnairCancelIneligibilityReason[] = [
          ...new Set(
            booking.eligibilities.voluntaryCancel
              .filter((eligibility) => !eligibility.isAllowedToUse)
              .map((eligibility) => eligibility.reason)
          ),
        ];

        return uniqueDisallowedReasons.map((reason) => {
          return this.getNotification(
            voluntaryCancelIneligibilityTranslations.get(reason) ?? 'cancelNotAllowed.notEligible',
            SvgLibraryIcon.ALERT_BLOCK
          );
        });
      })
    );
  }

  getChangeNotAllowedReasonsByBound(): Observable<Record<string, string>> {
    return this.store$.pipe(
      orderData(),
      map(({ eligibilities }) =>
        eligibilities.change
          .filter(({ isAllowedToUse }) => !isAllowedToUse)
          .reduce((all, { id, reason }) => {
            all[id] = changeIneligibilityTranslations.has(reason)
              ? `MMB.notifications.${changeIneligibilityTranslations.get(reason)}.text`
              : 'MMB.view.changeFlightsModal.disabledNotification';
            return all;
          }, {})
      ),
      startWith({})
    );
  }

  private getLocationData(bound: FinnairBoundItem, locations: FinnairOrder['locations']) {
    return {
      origin: getCityName(bound.departure.locationCode, locations),
      destination: getCityName(bound.arrival.locationCode, locations),
    };
  }

  private getNotification = (translationKey: string, iconName?: SvgLibraryIcon): NotificationInfo => {
    return {
      translationKey,
      show: true,
      theme: NotificationTheme.INFO,
      layout: NotificationLayout.BLOCK,
      iconName: iconName,
    };
  };

  private getWaitlistUpgradeTranslationKey(itinerary: FinnairItineraryItemFlight): string {
    if (itinerary.upgradeItem) {
      const moreThan48HoursToDeparture = TzDate.now().hoursTo(TzDate.of(itinerary.departure.dateTime)) > 48;
      return moreThan48HoursToDeparture
        ? `waitlistedUpgradeEcoTo.${itinerary.upgradeItem.cabinClass}.moreThan48HoursToDeparture`
        : 'waitlistedFlight';
    }
    return 'waitlistedFlightNoUpgrade';
  }
}
